import { rest } from "msw";
import type { RestHandler } from "msw";
import config from "../../config";
import { apiVersion } from "../../shared/constants/common.constant";
import type {
  RolePermission,
  RoleWithPermissionsListItem,
  RolesPermissionsListResponse,
} from "./permissions-api";
import type {
  UserPermissions,
  UserPermissionProfile,
  ApiPermissionsResponse,
  ApiPermissionItem,
} from "./permissions.types";
import {
  DEFAULT_PERMISSIONS,
  PermissionCode,
  permissionCodeToApiCode,
} from "./permissions.types";

const PERMISSION_CODES = Object.values(PermissionCode) as PermissionCode[];

const createPermissionItem = (
  id: number,
  code: string,
  name: string,
  category: string,
  isAllowed: boolean
): ApiPermissionItem => ({
  id,
  code,
  name,
  category,
  isAllowed,
});

const LABELS: Partial<
  Record<PermissionCode, { name: string; category: string }>
> = {
  [PermissionCode.AddToMonitoring]: {
    name: "Add to monitoring",
    category: "Monitoring",
  },
  [PermissionCode.AssignRoleToUsers]: {
    name: "Assign role to users",
    category: "Settings",
  },
  [PermissionCode.PerformBatchOperations]: {
    name: "Manage batch upload",
    category: "Batch",
  },
  [PermissionCode.ConfigurePreferences]: {
    name: "Configure Preferences",
    category: "Settings",
  },
  [PermissionCode.ConfigurePermissions]: {
    name: "Configure Permissions",
    category: "Settings",
  },
  [PermissionCode.CreateProfile]: {
    name: "Create a profile",
    category: "Profiles",
  },
  [PermissionCode.DeleteProfile]: {
    name: "Delete profile",
    category: "Profiles",
  },
  [PermissionCode.ModifyProfile]: { name: "Modify Profile", category: "Profiles" },
  [PermissionCode.ApproveDeclineKycStatus]: {
    name: "Approve or Decline KYC Status",
    category: "Profiles",
  },
  [PermissionCode.ExportProfile]: {
    name: "Export Profile",
    category: "Profiles",
  },
  [PermissionCode.Screen]: {
    name: "Screen",
    category: "Screening + Alerts",
  },
  [PermissionCode.SetStatus]: {
    name: "Set status",
    category: "Screening + Alerts",
  },
  [PermissionCode.SetRiskRating]: {
    name: "Set risk rating",
    category: "Screening + Alerts",
  },
  [PermissionCode.ModifyCriteria]: {
    name: "Modify Criteria",
    category: "Screening + Alerts",
  },
  [PermissionCode.ProcessMatches]: {
    name: "Process Matches",
    category: "Screening + Alerts",
  },
  [PermissionCode.DeleteRecord]: {
    name: "Delete a record",
    category: "Screening + Alerts",
  },
  [PermissionCode.RemoveFromMonitoring]: {
    name: "Remove from Monitoring",
    category: "Screening + Alerts",
  },
  [PermissionCode.AddProfileToBusinessMonitoring]: {
    name: "Add profile to Business Monitoring",
    category: "Business Monitoring",
  },
  [PermissionCode.ProcessBusinessMonitoringAlerts]: {
    name: "Process Business Monitoring Alerts",
    category: "Business Monitoring",
  },
  [PermissionCode.RemoveFromBusinessMonitoring]: {
    name: "Remove from Business Monitoring",
    category: "Business Monitoring",
  },
};

const createMockApiResponse = (
  merged: UserPermissions,
  roleId = 1,
  roleName = "Manager"
): ApiPermissionsResponse => ({
  roleId,
  roleName,
  teamId: "a1b2c3d4-0000-0000-0000-000000000001",
  teamName: "Default Group",
  permissions: PERMISSION_CODES.map((code, index) => {
    const meta = LABELS[code] ?? { name: code, category: "General" };
    return createPermissionItem(
      index + 1,
      permissionCodeToApiCode(code),
      meta.name,
      meta.category,
      merged[code] ?? false
    );
  }),
});

const permissionsFromFlags = (
  flags: Partial<Record<PermissionCode, boolean>>,
  defaultAllowed: boolean
): UserPermissions =>
  PERMISSION_CODES.reduce<UserPermissions>((acc, code) => {
    acc[code] = flags[code] ?? defaultAllowed;
    return acc;
  }, {});

export const MOCK_ALL_PERMISSIONS: UserPermissions = permissionsFromFlags(
  {},
  true
);

export const MOCK_READONLY_PERMISSIONS: UserPermissions = permissionsFromFlags(
  {},
  false
);

export const MOCK_ANALYST_PERMISSIONS: UserPermissions = permissionsFromFlags(
  {
    [PermissionCode.CreateProfile]: true,
    [PermissionCode.ModifyProfile]: true,
    [PermissionCode.Screen]: true,
    [PermissionCode.ProcessMatches]: true,
    [PermissionCode.ModifyCriteria]: true,
    [PermissionCode.AddToMonitoring]: true,
    [PermissionCode.SetRiskRating]: true,
    [PermissionCode.SetStatus]: true,
  },
  false
);

export const mockGetUserPermissions = (
  permissionsData?: UserPermissions,
  apiFailed = false,
  baseUrl: string = config.KYCP_AUTH_API_URL
): RestHandler => {
  const url = `${baseUrl}/${apiVersion}/users/me/roles/permissions`;

  return rest.get(url, async (_, res, ctx) => {
    if (apiFailed) {
      return res(
        ctx.status(500),
        ctx.json({ message: "Internal Server Error" })
      );
    }
    const merged = permissionsData ?? MOCK_ALL_PERMISSIONS;
    return res(ctx.status(200), ctx.json(createMockApiResponse(merged)));
  });
};

export const mockGetUserPermissionsNoCreate = (
  baseUrl: string = config.KYCP_AUTH_API_URL
): RestHandler =>
  mockGetUserPermissions(
    {
      ...MOCK_ALL_PERMISSIONS,
      [PermissionCode.CreateProfile]: false,
    },
    false,
    baseUrl
  );

export const mockGetUserPermissionsReadOnly = (
  baseUrl: string = config.KYCP_AUTH_API_URL
): RestHandler =>
  mockGetUserPermissions(MOCK_READONLY_PERMISSIONS, false, baseUrl);

export const mockGetUserPermissionsAnalyst = (
  baseUrl: string = config.KYCP_AUTH_API_URL
): RestHandler =>
  mockGetUserPermissions(MOCK_ANALYST_PERMISSIONS, false, baseUrl);

export const mockGetUserPermissionsNone = (
  baseUrl: string = config.KYCP_AUTH_API_URL
): RestHandler => mockGetUserPermissions(DEFAULT_PERMISSIONS, false, baseUrl);

export const mockGetUserPermissionProfile = (
  profileData?: Partial<UserPermissionProfile>,
  apiFailed = false
): RestHandler => {
  const url = `${config.COMPLIANCE_API_URL}/${apiVersion}/users/me/roles/permission-profile`;

  const defaultProfile: UserPermissionProfile = {
    userId: 12345,
    permissions: MOCK_ALL_PERMISSIONS,
    groups: [
      {
        teamId: "group-1",
        groupName: "Default Group",
        permissions: MOCK_ALL_PERMISSIONS,
      },
    ],
    lastUpdated: new Date().toISOString(),
  };

  return rest.get(url, async (_, res, ctx) => {
    if (apiFailed) {
      return res(
        ctx.status(500),
        ctx.json({ message: "Internal Server Error" })
      );
    }
    return res(
      ctx.status(200),
      ctx.json({ ...defaultProfile, ...profileData })
    );
  });
};

export const createPermissionsMock = (
  permissions: Partial<UserPermissions>,
  baseUrl: string = config.KYCP_AUTH_API_URL
): RestHandler => {
  const merged: UserPermissions = {
    ...MOCK_READONLY_PERMISSIONS,
    [PermissionCode.CreateProfile]: true,
  };
  for (const [key, value] of Object.entries(permissions)) {
    if (value !== undefined) {
      merged[key] = value;
    }
  }
  return mockGetUserPermissions(merged, false, baseUrl);
};

const rolePermissionsUrl = (roleId: number): string =>
  `${config.KYCP_AUTH_API_URL}/${apiVersion}/roles/${roleId}/permissions`;

const rolesPermissionsListUrl = (): string =>
  `${config.KYCP_AUTH_API_URL}/${apiVersion}/Roles/permissions`;

type RolePermissionMockTuple = readonly [
  id: number,
  isAllowed: boolean,
  code: string,
  name: string,
  category: string
];

const ROLE_PERMISSION_MOCK_TUPLES: readonly RolePermissionMockTuple[] = [
  [101, true, "createProfile", "Create a profile", "Profiles"],
  [102, false, "modifyProfile", "Modify Profile", "Profiles"],
  [
    103,
    true,
    "approveDeclineKycStatus",
    "Approve or Decline KYC Status",
    "Profiles",
  ],
  [104, true, "exportProfile", "Export Profile", "Profiles"],
  [105, true, "deleteProfile", "Delete profile", "Profiles"],
  [201, true, "screen", "Screen", "Screening + Alerts"],
  [202, true, "addToMonitoring", "Add to monitoring", "Screening + Alerts"],
  [
    203,
    true,
    "modifyCriteria",
    "Modify Criteria",
    "Screening + Alerts",
  ],
  [204, true, "processMatches", "Process Matches", "Screening + Alerts"],
  [205, true, "setRiskRating", "Set risk rating", "Screening + Alerts"],
  [206, true, "setStatus", "Set status", "Screening + Alerts"],
  [207, true, "deleteRecord", "Delete a record", "Screening + Alerts"],
  [
    208,
    true,
    "removeFromMonitoring",
    "Remove from Monitoring",
    "Screening + Alerts",
  ],
  [
    301,
    true,
    "addProfileToBusinessMonitoring",
    "Add profile to Business Monitoring",
    "Business Monitoring",
  ],
  [
    302,
    true,
    "processBusinessMonitoringAlerts",
    "Process Business Monitoring Alerts",
    "Business Monitoring",
  ],
  [
    303,
    true,
    "removeFromBusinessMonitoring",
    "Remove from Business Monitoring",
    "Business Monitoring",
  ],
  [401, true, "performBatchOperations", "Perform Batch Operations", "Batch"],
  [501, true, "configurePreferences", "Configure Preferences", "Settings"],
  [502, true, "assignRoleToUsers", "Assign role to users", "Settings"],
  [503, true, "configurePermissions", "Configure Permissions", "Settings"],
];

const ROLE_PERMISSION_MOCK_ITEMS: RolePermission[] =
  ROLE_PERMISSION_MOCK_TUPLES.map(([id, isAllowed, code, name, category]) => ({
    id: Number(id),
    isAllowed,
    code,
    name,
    category,
  }));

export const MOCK_ROLE_PERMISSION_FIXTURE: RolePermission[] =
  ROLE_PERMISSION_MOCK_ITEMS;

const ROLE_ID_TO_NAME: Record<number, string> = {
  1: "Admin",
  2: "Manager",
  3: "Analyst",
  4: "Viewer",
};

const createRoleListItem = (
  id: number,
  name: string,
  permissions: RolePermission[]
): RoleWithPermissionsListItem => ({
  id,
  name,
  permissions: permissions,
  modifiedAt: null as string | null,
  modifiedBy: null as string | null,
  modifiedById: null as number | null,
});

export const buildMockRolesPermissionsListResponse =
  (): RolesPermissionsListResponse => {
    const items = [
      createRoleListItem(1, "Admin", ROLE_PERMISSION_MOCK_ITEMS),
      createRoleListItem(2, "Manager", ROLE_PERMISSION_MOCK_ITEMS),
      createRoleListItem(3, "Analyst", ROLE_PERMISSION_MOCK_ITEMS),
      createRoleListItem(4, "Viewer", ROLE_PERMISSION_MOCK_ITEMS),
    ];
    return { items, count: items.length };
  };

export const mockGetRolesPermissionsList = (apiFailed = false): RestHandler =>
  rest.get(rolesPermissionsListUrl(), async (_, res, ctx) =>
    res(
      apiFailed ? ctx.status(500) : ctx.status(200),
      apiFailed
        ? ctx.json({ message: "Internal Server Error" })
        : ctx.json(buildMockRolesPermissionsListResponse())
    )
  );

export const mockGetRolePermissions = (
  roleId = 2,
  apiFailed = false
): RestHandler => {
  const roleName = ROLE_ID_TO_NAME[roleId] ?? "Role";
  const response = {
    roleId: roleId,
    roleName: roleName,
    permissions: ROLE_PERMISSION_MOCK_ITEMS,
  };

  return rest.get(rolePermissionsUrl(roleId), async (_req, res, ctx) =>
    res(apiFailed ? ctx.status(500) : ctx.status(200), ctx.json(response))
  );
};

export const mockPutRolePermissions = (
  roleId = 2,
  apiFailed = false
): RestHandler =>
  rest.put(rolePermissionsUrl(roleId), async (_, res, ctx) => {
    if (apiFailed) {
      return res(ctx.status(500));
    }
    return res(
      ctx.status(200),
      ctx.json({
        roleId: roleId,
        roleName: "string",
        permissions: ROLE_PERMISSION_MOCK_ITEMS,
      })
    );
  });
